import { useEffect, useRef } from "react";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import TrustStrip from "./components/TrustStrip";
import Problem from "./components/Problem";
import Clauses from "./components/Clauses";
import Steps from "./components/Steps";
import Platform from "./components/Platform";
import CompliAI from "./components/CompliAI";
import Deploy from "./components/Deploy";
import Report from "./components/Report";
import Resources from "./components/Resources";
import FAQ from "./components/FAQ";
import FinalCTA from "./components/FinalCTA";
import Footer from "./components/Footer";
import { BookDemoProvider } from "./components/BookDemoModal";

function ScrollRevealWrapper({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
    );

    const items = el.querySelectorAll(".reveal-item");
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return <div ref={ref}>{children}</div>;
}

export default function Home() {
  return (
    <BookDemoProvider>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <Hero />
          <ScrollRevealWrapper>
            <div className="reveal-item">
              <TrustStrip />
            </div>
            <div className="reveal-item">
              <Problem />
            </div>
            <div className="reveal-item">
              <Clauses />
            </div>
            <div className="reveal-item">
              <Steps />
            </div>
            <div className="reveal-item">
              <Platform />
            </div>
            <div className="reveal-item">
              <CompliAI />
            </div>
            <div className="reveal-item">
              <Deploy />
            </div>
            <div className="reveal-item">
              <Report />
            </div>
            <div className="reveal-item">
              <Resources />
            </div>
            <div className="reveal-item">
              <FAQ />
            </div>
            <div className="reveal-item">
              <FinalCTA />
            </div>
          </ScrollRevealWrapper>
        </main>
        <Footer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: "AI Compliance Checker — Enterprise EU AI Act Article 50 Assessment",
              description:
                "Map your entire AI platform against EU AI Act Article 50 with a built-in regulatory knowledge base. One auditable assessment report, traceable to source clauses, deployed on-prem.",
              url: "https://aiact.bridgeai.one/",
              dateModified: "2026-08-17",
              publisher: {
                "@type": "Organization",
                name: "AI Compliance Checker",
                url: "https://aiact.bridgeai.one/",
              },
              mainEntity: {
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "I'm not based in the EU. Does this apply?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes. If any of your users are in the EU, Article 50 applies to you — regardless of where your company is based.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "My product uses OpenAI / Claude. Am I the provider?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "For the model, no — but when you wrap it into a chatbot you ship under your own name, you become the provider of that interactive system. The disclosure (Art. 50.1) is on you.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "How does this scale across a large product portfolio?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "One assessment run covers your entire platform under a single dashboard, with each finding traceable to its source clause.",
                    },
                  },
                ],
              },
            }),
          }}
        />
      </div>
    </BookDemoProvider>
  );
}